<script setup lang="ts">
/**
 * Tutor chat inside the session overlay (PRD §6.8 PE-8.5, bead rl-9m4.5).
 *
 * Presentational: it owns the transcript and the composer, and nothing else.
 * Session state, the focus bar, the tick and the focus-block lifecycle live in
 * SessionOverlay — this panel only reports what a student did (`activity`) and
 * what completed (`exchange`).
 *
 * The tutor reply is one of the two genuine waits in the product (the other is
 * container cold start), so the "Thinking…" row is deliberately visible rather
 * than an instant-looking swap: pretending an 8s LLM turn is instant is how a
 * working session reads as broken.
 */

import { ArrowDown, LoaderCircle } from "lucide-vue-next";
import { renderMarkdown } from "@/lib/markdown";
import { nextTick, ref, watch } from "vue";

import { apiClient, ApiError } from "@/api/client";
import ResponseFeedback from "@/components/feedback/ResponseFeedback.vue";
import RlPill from "@/components/ui/RlPill.vue";

const props = defineProps<{
  sessionId: string;
  scopeLabel: string;
  openingMessage: string | null;
}>();

const emit = defineEmits<{ exchange: []; activity: [] }>();

interface ChatTurn {
  role: "assistant" | "user";
  content: string;
  // tutor-uox.1.1 — set on assistant turns only, so ResponseFeedback can key
  // its idempotency on (tutor_session_id, turn_index). Student turns are not
  // rateable and leave it undefined.
  turnIndex?: number;
}

// The first assistant message of a fresh session always lands at turn_index 0
// (see TutorSessionResponse in api/types.ts). The container hands us the text
// only, so the index is reconstructed here — getting it wrong would file the
// student's rating against the wrong message server-side.
const OPENING_TURN_INDEX = 0;

const turns = ref<ChatTurn[]>([]);
const draft = ref("");
const sending = ref(false);
const error = ref<string | null>(null);

const scroller = ref<HTMLElement | null>(null);
const composer = ref<HTMLTextAreaElement | null>(null);

// A student who has scrolled up is re-reading; yanking them back to the newest
// turn loses their place. `pinned` tracks whether they are still following the
// bottom, and `unreadBelow` offers the jump instead of forcing it.
const pinned = ref(true);
const unreadBelow = ref(false);
const PIN_SLACK_PX = 56;

// The container's idle timer must not tick down while someone is typing a long
// answer, but one event per keystroke is noise — one per second is enough to
// keep the timer honest.
const ACTIVITY_INTERVAL_MS = 1000;
let lastActivityAt = 0;

watch(
  () => props.openingMessage,
  (opening) => {
    if (!opening || turns.value.length > 0) return;
    turns.value.push({
      role: "assistant",
      content: opening,
      turnIndex: OPENING_TURN_INDEX,
    });
    scrollToLatest(true);
  },
  { immediate: true },
);

function renderMd(text: string): string {
  return renderMarkdown(text);
}

function onScroll(): void {
  const el = scroller.value;
  if (!el) return;
  pinned.value =
    el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_SLACK_PX;
  if (pinned.value) unreadBelow.value = false;
}

/** `force` is for turns the student caused themselves — following their own
 * send down is expected; following a tutor reply down while they read is not. */
function scrollToLatest(force = false): void {
  if (!force && !pinned.value) {
    unreadBelow.value = true;
    return;
  }
  void nextTick(() => {
    const el = scroller.value;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    pinned.value = true;
    unreadBelow.value = false;
  });
}

function reportActivity(): void {
  lastActivityAt = Date.now();
  emit("activity");
}

function onInput(): void {
  if (Date.now() - lastActivityAt < ACTIVITY_INTERVAL_MS) return;
  reportActivity();
}

function onKeydown(e: KeyboardEvent): void {
  // isComposing guards IME candidate selection, where Enter commits the
  // candidate rather than the message.
  if (e.key !== "Enter" || e.shiftKey || e.isComposing) return;
  e.preventDefault();
  void send();
}

function messageFor(e: unknown): string {
  if (e instanceof ApiError) {
    // `API error 429` tells a student nothing; the tutor cap route answers
    // with FastAPI's `detail`, a sentence written for them (cap reached,
    // retry window), which lands in the same slot a §10 code would.
    return e.code ?? e.message;
  }
  return e instanceof Error ? e.message : "Could not send your message.";
}

async function send(): Promise<void> {
  const text = draft.value.trim();
  if (!text || sending.value) return;

  sending.value = true;
  error.value = null;
  reportActivity();
  turns.value.push({ role: "user", content: text });
  draft.value = "";
  scrollToLatest(true);

  try {
    const res = await apiClient.sendTutorMessage(props.sessionId, text);
    turns.value.push({
      role: "assistant",
      content: res.content,
      turnIndex: res.turn_index,
    });
    // One event per completed round trip: the container counts exchanges from
    // this, so an event on a failed send would inflate the session summary.
    emit("exchange");
    scrollToLatest();
  } catch (e) {
    // The turn never reached the server, so roll the optimistic bubble back and
    // hand the text to the composer. Losing a long typed answer to a 429 is the
    // one failure a student will not forgive.
    const last = turns.value.length - 1;
    if (turns.value[last]?.role === "user") turns.value.splice(last, 1);
    draft.value = text;
    error.value = messageFor(e);
    void nextTick(() => composer.value?.focus());
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <!--
    PE-9.5 — tutor questions and typed answers must never reach analytics. The
    `/subjects/:id` URL filter keeps autocapture off this surface and there is
    no session replay; the marker states the rule on the element, and is kept
    local so the panel is safe wherever it is mounted.
  -->
  <section class="tutor-panel" data-statsig-block>
    <header class="head">
      <span class="eyebrow">Tutor</span>
      <!-- rl-yo3.18 — tutor mode has an explicit scope; say so, so the
           student knows what this chat is about. -->
      <RlPill tone="info" data-testid="tutor-scope">{{ scopeLabel }}</RlPill>
    </header>

    <div
      ref="scroller"
      class="transcript"
      data-testid="tutor-transcript"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      :aria-busy="sending"
      @scroll="onScroll"
    >
      <div
        v-for="(turn, i) in turns"
        :key="i"
        class="turn"
        :class="turn.role === 'assistant' ? 'turn-tutor' : 'turn-student'"
        data-testid="tutor-turn"
      >
        <div class="bubble" v-html="renderMd(turn.content)" />
        <ResponseFeedback
          v-if="turn.role === 'assistant' && turn.turnIndex !== undefined"
          :session-id="sessionId"
          :turn-index="turn.turnIndex"
        />
      </div>

      <div v-if="sending" class="turn turn-tutor" data-testid="tutor-thinking">
        <div class="bubble thinking">
          <LoaderCircle class="rl-spin" :size="14" aria-hidden="true" />
          <span>Thinking&hellip;</span>
        </div>
      </div>
    </div>

    <button
      v-if="unreadBelow"
      type="button"
      class="jump rl-anim-fade-up"
      data-testid="tutor-jump"
      @click="scrollToLatest(true)"
    >
      <ArrowDown :size="13" aria-hidden="true" />
      New reply
    </button>

    <p v-if="error" class="error" role="alert" data-testid="tutor-error">
      {{ error }}
    </p>

    <div class="composer">
      <textarea
        ref="composer"
        v-model="draft"
        rows="2"
        :disabled="sending"
        placeholder="Type your answer or ask a question&hellip;"
        data-testid="tutor-composer"
        @input="onInput"
        @keydown="onKeydown"
      />
      <button
        type="button"
        class="send"
        :disabled="sending || !draft.trim()"
        data-testid="tutor-send"
        @click="send"
      >
        Send
      </button>
    </div>
  </section>
</template>

<style scoped>
.tutor-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 380px;
  overflow: hidden;
  background: var(--rl-bg);
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  border-bottom: 1px solid var(--rl-border-subtle);
}

.eyebrow {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--rl-fg-3);
}

.transcript {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--sp-3);
  min-height: 0;
  padding: var(--sp-4) var(--sp-5);
  overflow-y: auto;
  background: var(--rl-bg-subtle);
}

.turn {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  max-width: 78%;
}

.turn-tutor {
  align-self: flex-start;
}

.turn-student {
  align-self: flex-end;
}

.bubble {
  padding: var(--sp-3) var(--sp-4);
  font-size: var(--rl-text-md);
  line-height: 1.55;
  text-wrap: pretty;
}

.turn-tutor .bubble {
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  border: 1px solid var(--rl-border-subtle);
  border-radius: var(--radius) var(--radius) var(--radius) var(--radius-sm);
}

.turn-student .bubble {
  color: var(--rl-neutral-25);
  background: var(--rl-neutral-900);
  border-radius: var(--radius) var(--radius) var(--radius-sm) var(--radius);
}

.bubble :deep(p) {
  margin: 0 0 var(--sp-2);
}

.bubble :deep(p:last-child) {
  margin-bottom: 0;
}

.bubble :deep(ul),
.bubble :deep(ol) {
  margin: 0 0 var(--sp-2);
  padding-left: var(--sp-5);
}

.bubble :deep(pre) {
  overflow-x: auto;
}

.thinking {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--rl-text-sm);
  font-style: italic;
  color: var(--rl-fg-3);
}

.jump {
  position: absolute;
  bottom: 96px;
  left: 50%;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: var(--sp-1) var(--sp-3);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-sm);
  font-weight: 600;
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  border: 1px solid var(--rl-border);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-md);
  transform: translateX(-50%);
  cursor: pointer;
}

.error {
  margin: 0;
  padding: var(--sp-2) var(--sp-5);
  font-size: var(--rl-text-sm);
  color: var(--rl-danger-fg);
  background: var(--rl-danger-bg);
}

.composer {
  display: flex;
  align-items: flex-end;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  border-top: 1px solid var(--rl-border-subtle);
}

.composer textarea {
  flex: 1;
  min-height: 44px;
  max-height: 110px;
  padding: var(--sp-3) var(--sp-4);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-md);
  line-height: 1.5;
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  border: 1px solid var(--rl-border);
  border-radius: var(--radius);
  resize: none;
}

.composer textarea:disabled {
  color: var(--rl-fg-3);
  background: var(--rl-bg-muted);
}

.send {
  flex: none;
  height: 44px;
  padding: 0 var(--sp-5);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-md);
  font-weight: 600;
  color: var(--rl-neutral-25);
  background: var(--rl-neutral-900);
  border: none;
  border-radius: var(--radius);
  cursor: pointer;
}

.send:disabled {
  background: var(--rl-neutral-400);
  cursor: not-allowed;
}
</style>
